import { useEffect, useState } from 'react';
import { CallCostCard } from '../components/CallCost';
import { EvaluationCard, isEvaluation } from '../components/Evaluation';
import { ApiError, type Api, type CallCost, type CallDetail, type CallTurn, type Counterpart } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { Gate } from '../ui';
import { callbackStateOf, duration, partyOf, resultOf, when } from './Calls';
import { ScoreBadge } from './Leads';

const factValue = (v: string | number | boolean, t: T): string => (typeof v === 'boolean' ? t(v ? 'common.yes' : 'common.no') : String(v).replaceAll('_', ' '));

/** m:ss from milliseconds (when a disclosure was said in the call). */
const mmss = (ms: number): string => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;

/**
 * BRAIN/34 P2: the other calls with this number (newest first, summaries only), and "Add to contacts" for a number
 * not in contacts (by hand, contacts permission; an unknown caller never becomes a contact by itself).
 */
export const CounterpartCard = ({ t, counterpart, onOpen, onSameNumber, onAdd }: {
  t: T; counterpart: Counterpart; onOpen: (id: string) => void; onSameNumber?: (() => void) | undefined; onAdd?: ((name: string) => Promise<'added' | 'exists'>) | undefined;
}) => {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'error'; text: string }>();
  const add = async () => {
    if (!onAdd) return;
    setBusy(true);
    setNote(undefined);
    try {
      const r = await onAdd(name.trim());
      setNote({ kind: 'ok', text: t(r === 'added' ? 'calls.counterpart.added' : 'calls.counterpart.exists') });
    } catch (e) {
      setNote({ kind: 'error', text: e instanceof ApiError && t(`error.${e.code}`) !== `error.${e.code}` ? t(`error.${e.code}`) : t('error.generic') });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card counterpart">
      <div className="row-between">
        <h3>{t('calls.counterpart.title')}</h3>
        <span className={`pill ${counterpart.contact ? 'ok' : 'muted'}`}>{t(counterpart.contact ? 'calls.counterpart.inContacts' : 'calls.counterpart.notInContacts')}</span>
      </div>
      {counterpart.total === 0 ? (
        <p className="muted">{t('calls.counterpart.none')}</p>
      ) : (
        <>
          <p className="hint">{t('calls.counterpart.counts', { total: counterpart.total, before: counterpart.before })}</p>
          <div className="list counterpart-list">
            {counterpart.recent.map((x) => {
              const r = resultOf({ status: x.status, endedBy: x.endedBy, durationSeconds: x.durationSeconds, outcome: (x.outcome ?? null) as CallDetail['outcome'] }, t);
              return (
                <div key={x.id} className="list-row clickable" onClick={() => onOpen(x.id)}>
                  <div className="grow">
                    <div className="sub">{when(x.createdAt)} · {t(`direction.${x.direction}`)} · {x.agentName} · {duration(x.durationSeconds)}</div>
                    <div className="summary-line">{x.summary ?? '—'}</div>
                  </div>
                  <span className={`pill ${r.kind}`}>{r.text}</span>
                </div>
              );
            })}
          </div>
          {onSameNumber && <button type="button" className="link" onClick={onSameNumber}>{t('calls.counterpart.all')} <span className="arrow">→</span></button>}
        </>
      )}
      {!counterpart.contact && onAdd && (
        <Gate need={PERM.contacts}>
          <div className="inline-form section-gap">
            <input value={name} maxLength={120} placeholder={t('calls.counterpart.name')} aria-label={t('calls.counterpart.name')} onChange={(e) => setName(e.target.value)} />
            <button type="button" className="secondary" disabled={busy} onClick={() => void add()}>{t('calls.counterpart.add')}</button>
          </div>
          <p className="hint">{t('calls.counterpart.addHint')}</p>
        </Gate>
      )}
      {note && <div className={`banner ${note.kind} small`}>{note.text}</div>}
    </div>
  );
};

/** One call: summary (outcome, callback, what was collected), the conversation and the recording. */
export const CallCard = ({ api, t, callId, onOpen, onSameNumber }: { api: Api; t: T; callId: string; onOpen: (id: string) => void; onSameNumber?: ((callId: string) => void) | undefined }) => {
  // ADR-071: the recording and the full transcript need centeros.recordings (the server sends neither without it),
  // so those tabs are not offered instead of reading "no recording" / "no turns".
  const canHear = useCan()(PERM.recordings);
  const [call, setCall] = useState<CallDetail>();
  const [turns, setTurns] = useState<CallTurn[]>([]);
  const [cost, setCost] = useState<CallCost | null>(null);
  const [counterpart, setCounterpart] = useState<Counterpart | null>(null);
  const [audio, setAudio] = useState<string>();
  const [error, setError] = useState<string>();
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<'summary' | 'transcript' | 'recording'>('summary');
  // The recording is fetched only once the Recording tab is opened: each fetch is logged as a listen (activity log).
  const [listen, setListen] = useState(false);

  useEffect(() => {
    setCall(undefined);
    setAudio(undefined);
    setError(undefined);
    api
      .callDetail(callId)
      .then((r) => {
        setCall(r.call);
        setTurns(r.turns);
        setCost(r.cost ?? null);
        setCounterpart(r.counterpart ?? null);
        // The business's own labels for the facts it collects.
        api
          .agent(r.call.agentId)
          .then((a) => {
            const facts = (a.spec?.spec as { facts?: Array<{ key: string; label: string }> } | undefined)?.facts ?? [];
            setLabels(Object.fromEntries(facts.map((f) => [f.key, f.label])));
          })
          .catch(() => undefined);
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.code : 'generic'));
  }, [api, callId]);

  useEffect(() => {
    if (!listen || !call?.recording) return;
    let url: string | undefined;
    api
      .recording(callId)
      .then((b) => {
        url = URL.createObjectURL(b);
        setAudio(url);
      })
      .catch(() => setAudio(undefined));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [api, callId, call?.recording, listen]);

  if (error) return <div className="banner error">{t(`error.${error}`) === `error.${error}` ? `${t('error.generic')} (${error})` : t(`error.${error}`)}</div>;
  if (!call) return <div className="card muted">{t('boot.loading')}</div>;
  const r = resultOf({ ...call }, t);
  const facts = Object.entries(call.outcome?.facts ?? {});
  const spoken = turns.filter((x) => x.customer).length;
  type Tab = 'summary' | 'transcript' | 'recording';
  const tabs: readonly Tab[] = canHear ? ['summary', 'transcript', 'recording'] : ['summary'];
  const shown: Tab = tabs.includes(tab) ? tab : 'summary';

  return (
    <div className="call-card">
      <div className="call-card-head">
        <div>
          <div className="kicker">{t('calls.card')}</div>
          {counterpart?.contact?.name ? (
            <>
              <h2><bdi>{counterpart.contact.name}</bdi></h2>
              <div className="row">
                <span className="mono num">{counterpart.partyE164}</span>
                <span className="pill">{t(`leads.stage.${counterpart.contact.stage}`) === `leads.stage.${counterpart.contact.stage}` ? counterpart.contact.stage : t(`leads.stage.${counterpart.contact.stage}`)}</span>
              </div>
            </>
          ) : (
            <h2 className="mono-title">{partyOf(call, t)}</h2>
          )}
          <div className="hint">{when(call.createdAt)} · {t(`direction.${call.direction}`)}</div>
        </div>
        <span className={`pill ${r.kind}`}>{r.text}</span>
      </div>
      <div className="tiles">
        <div className="tile"><span>{t('calls.tile.duration')}</span><strong>{duration(call.durationSeconds)}</strong></div>
        {canHear && <div className="tile"><span>{t('calls.tile.recording')}</span><strong>{call.recording ? t('calls.tile.ready') : '—'}</strong></div>}
        {canHear && <div className="tile"><span>{t('calls.tile.turns')}</span><strong>{spoken}</strong></div>}
        <div className="tile"><span>{t('calls.tile.ended')}</span><strong>{call.endedBy ? t(`calls.endedBy.${call.endedBy}`) : '—'}</strong></div>
      </div>
      {call.parentCallId && <p><button type="button" className="link" onClick={() => onOpen(call.parentCallId!)}>{t('callback.fromCall')}</button></p>}
      {tabs.length > 1 && (
        <div className="tabs compact">
          {tabs.map((x) => (
            <button key={x} type="button" className={`tab ${shown === x ? 'active' : ''}`} onClick={() => { setTab(x); if (x === 'recording') setListen(true); }}>{t(`calls.cardTab.${x}`)}</button>
          ))}
        </div>
      )}
      {shown === 'summary' && (
        <>
          {call.analysis && (
            <div className="card analysis">
              <div className="row-between">
                <h3>{t('calls.analysis')}</h3>
                <span className="row">
                  <span className={`pill sentiment ${call.analysis.sentiment}`}>{t(`calls.sentiment.${call.analysis.sentiment}`)}</span>
                  <ScoreBadge score={call.analysis.score} t={t} />
                </span>
              </div>
              <p>{call.analysis.summary}</p>
              {call.analysis.nextAction && <p className="next-action"><span className="muted">{t('calls.nextAction')}</span> {call.analysis.nextAction}</p>}
            </div>
          )}
          {counterpart && (
            <CounterpartCard
              t={t}
              counterpart={counterpart}
              onOpen={onOpen}
              onSameNumber={onSameNumber ? () => onSameNumber(callId) : undefined}
              onAdd={async (name) => {
                const r = await api.contactFromCall(callId, name);
                setCounterpart({ ...counterpart, contact: { id: r.contact.id, name: r.contact.name, stage: counterpart.contact?.stage ?? 'new' } });
                return r.created ? 'added' : 'exists';
              }}
            />
          )}
          {isEvaluation(call.evaluation) && <EvaluationCard t={t} e={call.evaluation} />}
          {cost && <CallCostCard t={t} cost={cost} />}
          {call.outcome?.callback && (
            <div className="card highlight">
              <h3>{t('calls.callback')}</h3>
              <p><strong>{call.outcome.callback.when}</strong>{call.outcome.callback.note ? <span className="muted"> · {call.outcome.callback.note}</span> : null}</p>
              <p>{((x) => <span className={`pill ${x.kind}`}>{x.text}</span>)(callbackStateOf(call, t))}</p>
              {call.callbackCallId && <button type="button" className="link" onClick={() => onOpen(call.callbackCallId!)}>{t('callback.openCall')}</button>}
            </div>
          )}
          {(call.recordingPurgedAt || call.contentPurgedAt) && (
            <div className="banner warn small">{t('privacy.callPurged', { at: new Date((call.contentPurgedAt ?? call.recordingPurgedAt)!).toLocaleDateString() })}</div>
          )}
          {call.outcome?.compliance && (
            <div className="card">
              <h3>{t('compliance.title')}</h3>
              <ul className="compliance-list">
                {call.outcome.compliance.done.map((d) => (
                  <li key={d.id} title={d.said}>
                    <span className={`pill ${call.outcome!.compliance!.late.includes(d.id) ? 'warn' : 'ok'}`}>✓ {mmss(d.atMs)}</span>{' '}
                    {t(`compliance.item.${d.id}`) === `compliance.item.${d.id}` ? d.id.replaceAll('_', ' ') : t(`compliance.item.${d.id}`)}
                    {call.outcome!.compliance!.late.includes(d.id) && <span className="hint"> · {t('compliance.late')}</span>}
                  </li>
                ))}
                {call.outcome.compliance.missing.map((id) => (
                  <li key={id}>
                    <span className="pill error">✗</span>{' '}
                    {t(`compliance.item.${id}`) === `compliance.item.${id}` ? id.replaceAll('_', ' ') : t(`compliance.item.${id}`)}
                    <span className="hint"> · {t('compliance.missing')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="card">
            <h3>{t('calls.facts')}</h3>
            {facts.length === 0 && <p className="muted">{t('calls.noFacts')}</p>}
            {facts.length > 0 && (
              <dl className="facts">
                {facts.map(([k, v]) => (
                  <div key={k}>
                    <dt>{labels[k] ?? k.replaceAll('_', ' ')}</dt>
                    <dd>{factValue(v, t)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </>
      )}
      {shown === 'transcript' && (
        <div className="card transcript">
          {turns.length === 0 && <p className="muted">{t('calls.noTurns')}</p>}
          {turns.map((x) => (
            <div key={x.index} className="turn-pair">
              {x.customer && <div className="bubble user">{x.customer}</div>}
              {x.event === 'caller_silent' && <div className="event">{t('calls.event.silent')}</div>}
              {x.agent.length > 0 && <div className="bubble assistant">{x.agent.join(' ')}</div>}
            </div>
          ))}
        </div>
      )}
      {shown === 'recording' && (
        <div className="card">
          {!call.recording && <p className="muted">{t('calls.noRecording')}</p>}
          {call.recording && !audio && <p className="muted">{t('boot.loading')}</p>}
          {audio && <audio controls src={audio} className="player" />}
          {call.recording && <p className="hint">{t('calls.recording.hint')}</p>}
          {call.recording?.truncated && <p className="hint">{t('calls.recordingTruncated')}</p>}
        </div>
      )}
    </div>
  );
};

export const CallDetailPage = ({ api, t, callId, onBack, onOpen, onSameNumber }: { api: Api; t: T; callId: string; onBack: () => void; onOpen: (id: string) => void; onSameNumber?: ((callId: string) => void) | undefined }) => (
  <section>
    <button type="button" className="link back" onClick={onBack}>← {t('calls.back')}</button>
    <CallCard api={api} t={t} callId={callId} onOpen={onOpen} onSameNumber={onSameNumber} />
  </section>
);
